import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Slide,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { CloseIcon, colors, palette } from '@silbo/ui';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { format, isValid, set } from 'date-fns';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBandwidthContext } from '../../../contexts/BandwidthContext';
import { useConfigContext } from '../../../contexts/ConfigContext/useConfigContext';
import { useQueryQueueContext } from '../../../contexts/QueryQueueContext';
import FullHeightDialog from '../components/FullHeightDialog';
import RequestDetailsContent from '../components/RequestDetailsContent';
import { useAcceptRequestMutation, useRejectRequestMutation } from '../hooks/mutations';
import { getRequestQueryOptions } from '../queries/get-request/get-request-query-options';

interface RequestDetailsPageProps {
  requestId: string;
}

const RequestDetailsPage = ({ requestId }: RequestDetailsPageProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation('bed-management');
  const { data: request } = useSuspenseQuery(getRequestQueryOptions(requestId));

  const { addQuery, removeQuery, queryAdded, setQueryAdded } = useQueryQueueContext();
  const { status } = useBandwidthContext();
  const { config } = useConfigContext();
  const isOffline = status === 'offline';

  const handleClose = () => {
    setQueryAdded(false);
    navigate({ to: '/bed-management/requests' });
  };

  const actionInitiatedRef = useRef(false);

  useEffect(() => {
    if (isOffline && queryAdded && actionInitiatedRef.current) {
      actionInitiatedRef.current = false;
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOffline, queryAdded]);

  const [showForm, setShowForm] = useState(false);
  const [action, setAction] = useState<'accept' | 'reject' | ''>('');
  const [rejectReason, setRejectReason] = useState('');
  const [acceptDate, setAcceptDate] = useState<Date>(new Date());

  const handleActionClick = (actionType: 'accept' | 'reject') => {
    setShowForm(true);
    setAction(actionType);
  };

  const handleRejectReasonChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setRejectReason(event.target.value);
  };

  const handleAcceptTimeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const time = event.target.value;
    const [hours, minutes] = time.split(':').map(Number);

    setAcceptDate((prevDate) =>
      set(prevDate, {
        hours,
        minutes,
      })
    );
  };

  const handleAcceptDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const date = new Date(event.target.value);

    if (isValid(date)) {
      setAcceptDate((prevDate) =>
        set(prevDate, {
          year: date.getFullYear(),
          month: date.getMonth(),
          date: date.getDate(),
        })
      );
    }
  };

  const handleHideForm = () => {
    setShowForm(false);
    setAction('');
    setRejectReason('');
    setAcceptDate(new Date());
  };

  const { acceptRequest, isPending: isAcceptPending } = useAcceptRequestMutation({
    onSuccess: (_, variables) => {
      removeQuery(JSON.stringify({ requestId: variables.id, action: 'accept' }));
      handleClose();
    },
    onError: (_, variables) => {
      removeQuery(JSON.stringify({ requestId: variables.id, action: 'accept' }));
    },
  });

  const { rejectRequest, isPending: isRejectPending } = useRejectRequestMutation({
    onSuccess: (_, variables) => {
      removeQuery(JSON.stringify({ requestId: variables.id, action: 'reject' }));
      handleClose();
    },
    onError: (_, variables) => {
      removeQuery(JSON.stringify({ requestId: variables.id, action: 'reject' }));
    },
  });

  const isMutationPending = isAcceptPending || isRejectPending;

  const handleConfirmAction = () => {
    actionInitiatedRef.current = true;

    if (action === 'accept' && acceptDate) {
      const queryId = JSON.stringify({ requestId: request.id, action: 'accept' });

      addQuery({
        id: queryId,
        queryFn: async () => {
          await acceptRequest({ id: request.id, startAt: acceptDate });
        },
      });
    } else if (action === 'reject') {
      const queryId = JSON.stringify({ requestId: request.id, action: 'reject' });

      addQuery({
        id: queryId,
        queryFn: async () => {
          await rejectRequest({ id: request.id, reason: rejectReason });
        },
      });
    }
  };

  return (
    <FullHeightDialog open onClose={handleClose}>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h2">{t('requestDetails.title')}</Typography>
          <IconButton onClick={handleClose} edge="end">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <RequestDetailsContent request={request} />
      </DialogContent>
      <Slide direction="up" in={showForm} onExited={handleHideForm} mountOnEnter unmountOnExit>
        <Box
          width="100%"
          borderTop="1px solid"
          borderBottom="1px solid"
          borderColor={colors.grey[10]}
          bgcolor={palette.background.default}
        >
          <Stack spacing={3} justifyContent="center" paddingX={6} paddingY={4}>
            <Stack direction="row" gap={2}>
              <Typography variant="h6">
                {action === 'accept'
                  ? t('requestDetails.actions.accept.selectStartTime')
                  : t('requestDetails.actions.reject.selectReason')}
              </Typography>
              <IconButton onClick={() => setShowForm(false)} edge="end" sx={{ marginTop: -2 }}>
                <CloseIcon />
              </IconButton>
            </Stack>
            {action === 'accept' ? (
              <Stack direction="row" alignItems="center" gap={2}>
                <TextField
                  type="date"
                  value={format(acceptDate, 'yyyy-MM-dd')}
                  onChange={handleAcceptDateChange}
                  sx={{
                    '& .MuiInputBase-input': {
                      display: 'flex',
                    },
                  }}
                />
                <TextField
                  type="time"
                  value={format(acceptDate, 'HH:mm')}
                  onChange={handleAcceptTimeChange}
                  sx={{
                    '& .MuiInputBase-input': {
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    },
                  }}
                />
              </Stack>
            ) : (
              <TextField
                placeholder={t('requestDetails.actions.reject.placeholder')}
                autoFocus
                multiline
                minRows={5}
                value={rejectReason}
                onChange={handleRejectReasonChange}
              />
            )}
          </Stack>
        </Box>
      </Slide>
      <DialogActions
        sx={{
          flexDirection: 'row',
          '&>button:not(:last-child)': { marginBottom: 0 },
          gap: 2,
          bgcolor: palette.background.default,
          minHeight: 100,
          zIndex: 1,
        }}
      >
        {action ? (
          <Button onClick={() => handleConfirmAction()} disabled={isMutationPending} fullWidth>
            {isMutationPending ? <CircularProgress size={24} color="inherit" /> : t('requestDetails.actions.confirm')}
          </Button>
        ) : (
          config?.answerMovementRequest && (
            <>
              {request.request.requestType !== 'DIRECT_ADMISSION' && (
                <Button onClick={() => handleActionClick('reject')} variant="outlined" fullWidth>
                  {t('requestDetails.actions.reject.label')}
                </Button>
              )}
              <Button onClick={() => handleActionClick('accept')} fullWidth>
                {t('requestDetails.actions.accept.label')}
              </Button>
            </>
          )
        )}
      </DialogActions>
    </FullHeightDialog>
  );
};

export default RequestDetailsPage;
